import { useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import type { ValidateResult } from 'react-hook-form';

// Single-arg async validate rule — this hook only needs the value.
type ValidateFn = (value: string) => Promise<ValidateResult>;
type OnLeadingEdge = (value: string) => void;

interface Deferred<T> {
	promise: Promise<T>;
	resolve: (value: T) => void;
	reject: (reason?: unknown) => void;
}

const createDeferred = <T>(): Deferred<T> => {
	let resolve!: (value: T) => void;
	let reject!: (reason?: unknown) => void;
	const promise = new Promise<T>((res, rej) => {
		resolve = res;
		reject = rej;
	});
	return { promise, resolve, reject };
};

export interface DebouncedValidate {
	(value: string): Promise<ValidateResult>;
	cancel: () => void;
}

// Debounces an async RHF validate function by `delay` ms.
// All calls within the window share one Promise, so no zombie pending validations.
// Drops straight into rules={{ validate: useDebouncedValidate(fn) }}.
export const useDebouncedValidate = (
	fn: ValidateFn,
	delay = 500,
	onLeadingEdge?: OnLeadingEdge,
): DebouncedValidate => {
	const validateFnRef = useRef(fn);
	const onLeadingEdgeRef = useRef(onLeadingEdge);

	// Update the refs on every render so the callbacks are always current
	// without changing the debounced wrapper's identity.
	useLayoutEffect(() => {
		validateFnRef.current = fn;
		onLeadingEdgeRef.current = onLeadingEdge;
	}, [fn, onLeadingEdge]);

	const debounced = useMemo<DebouncedValidate>(() => {
		let timer: ReturnType<typeof setTimeout> | undefined;
		let latestValue: string | undefined;
		let pending: Deferred<ValidateResult> | undefined;

		const runValidation = () => {
			const value = latestValue;
			const deferred = pending;
			pending = undefined;
			timer = undefined;
			if (value === undefined || !deferred) {
				return;
			}
			validateFnRef.current(value).then(deferred.resolve).catch(deferred.reject);
		};

		const validate = (value: string): Promise<ValidateResult> => {
			latestValue = value;
			if (timer) {
				clearTimeout(timer);
			}
			if (!pending) {
				onLeadingEdgeRef.current?.(value);
				pending = createDeferred<ValidateResult>();
			}
			timer = setTimeout(runValidation, delay);
			return pending.promise;
		};

		const cancel = () => {
			if (timer) {
				clearTimeout(timer);
				timer = undefined;
			}
			pending = undefined;
		};

		return Object.assign(validate, { cancel });
	}, [delay]);

	useEffect(() => () => debounced.cancel(), [debounced]);

	return debounced;
};
